import { debounce } from "lodash";
import { useCallback } from "react";
import CreateCharacterMpProvider from "@/core/data-provider/data-mp/create-character/create-character-mp-provider";
import useAppDispatch from "@/core/helpers/useAppDispatch";
import useAppSelector from "@/core/helpers/useAppSelector";
import {
  HAIR_COLORS,
  HAIR_STYLE_KEYS,
  HEAD_OVERLAY,
  MAKEUP_OVERLAY_COLORS,
} from "@/domains/createCharacter/constants/headOverlay";
import {
  configSelector,
  hairSelector,
  personalDataSelector,
  setHair,
  setHeadOverlay,
} from "@/domains/createCharacter/slice";
import randomArrayIndex from "@/domains/createCharacter/helpers/randomArrayIndex";

export default function useChangeHairStyle() {
  const dispatch = useAppDispatch();
  const { gender } = useAppSelector(personalDataSelector);
  const hair = useAppSelector(hairSelector);
  const config = useAppSelector((state) => configSelector(state, gender));

  const onChangeHair = useCallback(
    async (variant) => {
      await CreateCharacterMpProvider.setHair({
        drawableId: variant,
      });
      dispatch(setHair({ drawableId: variant }));
    },
    [dispatch],
  );

  const onChangeHairColor = useCallback(
    async (color: number) => {
      await CreateCharacterMpProvider.setHairColor({
        color,
        highlightColor: hair.highlightColor,
      });

      dispatch(setHair({ color }));
    },
    [dispatch, hair],
  );

  const onChangeHairHighlightColor = useCallback(
    async (highlightColor: number) => {
      await CreateCharacterMpProvider.setHairColor({
        color: hair.color,
        highlightColor,
      });

      dispatch(setHair({ highlightColor }));
    },
    [dispatch, hair],
  );

  const randomizeAll = useCallback(
    debounce(async () => {
      const randomDrawableId = config.hairs[randomArrayIndex(config.hairs.length)];
      const randomHairColor = randomArrayIndex(HAIR_COLORS.length);
      const randomHairHighlightColor = randomArrayIndex(HAIR_COLORS.length);

      if (randomDrawableId) {
        await CreateCharacterMpProvider.setHair({
          drawableId: randomDrawableId,
        });

        await CreateCharacterMpProvider.setHairColor({
          color: randomHairColor,
          highlightColor: randomHairHighlightColor,
        });

        dispatch(
          setHair({
            drawableId: randomDrawableId,
            color: randomHairColor,
            highlightColor: randomHairHighlightColor,
          }),
        );
      }

      const headOverlay: Partial<
        Record<
          string,
          {
            overlayId: number;
            index: number;
            opacity: number;
            firstColor: number;
            secondColor: number;
          }
        >
      > = {};

      HAIR_STYLE_KEYS.forEach((feature) => {
        const variants = [
          HEAD_OVERLAY[feature].default,
          ...new Array(HEAD_OVERLAY[feature].max).fill(null).map((_, v) => v),
        ];
        const randomVariant = variants[Math.floor(Math.random() * variants.length)];

        headOverlay[feature] = {
          overlayId: HEAD_OVERLAY[feature].id,
          index: randomVariant,
          opacity: Math.floor(Math.random() * 100) / 100,
          firstColor: Math.floor(Math.random() * MAKEUP_OVERLAY_COLORS.length),
          secondColor: Math.floor(Math.random() * MAKEUP_OVERLAY_COLORS.length),
        };
      });

      await Promise.allSettled(
        Object.entries(headOverlay).map(([_, value]) =>
          CreateCharacterMpProvider.setHeadOverlay(value),
        ),
      );
      dispatch(setHeadOverlay(headOverlay));
    }, 50),
    [dispatch, config],
  );

  return {
    config,
    gender,
    hair,
    onChangeHair,
    onChangeHairColor,
    onChangeHairHighlightColor,
    randomizeAll,
  };
}
